import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

import { API_URL } from '../utils/config';
import axios from 'axios';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  
  const [defaultCity, setDefaultCity] = useState('');
  const [shippingFee, setShippingFee] = useState(130);

  // NEW: Track which items are selected (all selected by default)
  const [selectedItems, setSelectedItems] = useState([]);

  // Initialize all items as selected when cart loads
  useEffect(() => {
    setSelectedItems(cartItems.map(item => item.cartItemId || item._id));
  }, [cartItems]);

  // Fetch default address to compute shipping fee
  useEffect(() => {
    const fetchDefaultAddress = async () => {
      if (isAuthenticated && token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const addresses = response.data.addresses || [];
          const defaultAddr = addresses.find(addr => addr.isDefault) || addresses[0];
          if (defaultAddr && defaultAddr.city) {
            setDefaultCity(defaultAddr.city);
            setShippingFee(defaultAddr.city.trim().toLowerCase() === 'khulna' ? 80 : 130);
          } else {
            setDefaultCity('');
            setShippingFee(130);
          }
        } catch (error) {
          console.error('Error fetching user info for shipping fee:', error);
          setShippingFee(130);
        }
      } else {
        setDefaultCity('');
        setShippingFee(130);
      }
    };
    fetchDefaultAddress();
  }, [isAuthenticated, token]);

  // NEW: Helper to fix image URLs
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/img/products/f1.jpg';
    if (imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  // NEW: Toggle selection for a single item
  const toggleItemSelection = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  // NEW: Select/Deselect all items
  const toggleSelectAll = () => {
    if (selectedItems.length === cartItems.length) {
      setSelectedItems([]); // Deselect all
    } else {
      setSelectedItems(cartItems.map(item => item.cartItemId || item._id)); // Select all
    }
  };

  // NEW: Calculate total for SELECTED items only
  const getSelectedTotal = () => {
    return cartItems
      .filter(item => selectedItems.includes(item.cartItemId || item._id))
      .reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // NEW: Get selected items for checkout
  const getSelectedItemsData = () => {
    return cartItems.filter(item => selectedItems.includes(item.cartItemId || item._id));
  };

  // NEW: Handle checkout
  const handleCheckout = () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item to checkout!');
      return;
    }

    if (!isAuthenticated) {
      alert('Please login to continue with checkout');
      navigate('/login');
      return;
    }

    // Navigate to checkout with selected items
    navigate('/checkout', { 
      state: { 
        selectedItems: getSelectedItemsData() 
      } 
    });
  };

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(productId, parseInt(newQuantity));
    }
  };

  return (
    <>
      <section id="page-header">
        <h2>Shopping Cart</h2>
        <p>Review your items and proceed to checkout</p>
      </section>

      <section id="cart" className="section-p1">
        {cartItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h2>Your cart is empty</h2>
            <p>Add some products to your cart to see them here.</p>
            <Link to="/shop">
              <button className="normal" style={{ marginTop: '20px' }}>
                Continue Shopping
              </button>
            </Link>
          </div>
        ) : (
          <>
            {/* NEW: Selection info and controls */}
            <div style={{ 
              background: 'white', 
              padding: '15px 20px', 
              marginBottom: '20px', 
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                <input
                  type="checkbox"
                  checked={selectedItems.length === cartItems.length && cartItems.length > 0}
                  onChange={toggleSelectAll}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>Select All ({cartItems.length} items)</span>
              </label>
              <span style={{ 
                color: '#088178', 
                fontWeight: '600',
                fontSize: '16px'
              }}>
                {selectedItems.length} of {cartItems.length} items selected
              </span>
            </div>

            <table width="100%">
              <thead>
                <tr>
                  {/* NEW: Select column */}
                  <td style={{ textAlign: 'center', fontWeight: '700' }}>Select</td>
                  <td>Image</td>
                  <td>Product</td>
                  <td>Price</td>
                  <td>Quantity</td>
                  <td>Subtotal</td>
                  <td>Remove</td>
                </tr>
              </thead>
              <tbody>
                {cartItems.map(item => {
                  const itemId = item.cartItemId || item._id;
                  return (
                    <tr 
                      key={itemId}
                      style={{
                        background: selectedItems.includes(itemId) ? '#e8f6f3' : 'transparent',
                        borderLeft: selectedItems.includes(itemId) ? '4px solid #088178' : 'none'
                      }}
                    >
                      {/* NEW: Checkbox column */}
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(itemId)}
                          onChange={() => toggleItemSelection(itemId)}
                          style={{ 
                            width: '18px', 
                            height: '18px', 
                            cursor: 'pointer',
                            accentColor: '#088178'
                          }}
                        />
                      </td>
                      <td>
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.name}
                          onError={(e) => {
                            e.target.src = '/img/products/f1.jpg';
                          }}
                        />
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '600' }}>{item.name}</div>
                          {(item.selectedColor || item.selectedSize) && (
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '4px', display: 'flex', gap: '10px' }}>
                              {item.selectedColor && <span>🎨 Color: <strong>{item.selectedColor}</strong></span>}
                              {item.selectedSize && <span>📏 Size: <strong>{item.selectedSize}</strong></span>}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{item.price}৳</td>
                      <td>
                        <input
                          type="number"
                          value={item.quantity}
                          min="1"
                          onChange={(e) => handleQuantityChange(itemId, e.target.value)}
                        />
                      </td>
                      <td>{item.price * item.quantity}৳</td>
                      <td>
                        <a href="#" onClick={(e) => {
                          e.preventDefault();
                          removeFromCart(itemId);
                        }}>
                          <i className="far fa-times-circle"></i>
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </section>

      {cartItems.length > 0 && (
        <section id="cart-ad" className="section-p1">
          <div id="coupon">
            <h3>Apply Coupon</h3>
            <div>
              <input type="text" placeholder="Enter Your Coupon" />
              <button className="normal">Apply</button>
            </div>
          </div>

          <div id="subtotal">
            <h3>Cart Total</h3>
            <table>
              <tbody>
                {/* NEW: Show selected items count */}
                <tr>
                  <td>Selected Items</td>
                  <td style={{ color: '#088178', fontWeight: '700' }}>
                    {selectedItems.length} items
                  </td>
                </tr>
                <tr>
                  <td>Cart Subtotal</td>
                  {/* NEW: Show total for SELECTED items only */}
                  <td>{getSelectedTotal()}৳</td>
                </tr>
                 <tr>
                  <td>Shipping {defaultCity ? `(${defaultCity})` : ''}</td>
                  <td>{shippingFee}৳</td>
                </tr>
                <tr>
                  <td><strong>Total</strong></td>
                  {/* NEW: Show total for SELECTED items only plus shipping */}
                  <td><strong>{getSelectedTotal() + shippingFee}৳</strong></td>
                </tr>
              </tbody>
            </table>
            
            {/* NEW: Updated checkout button */}
            <button 
              className="normal"
              onClick={handleCheckout}
              disabled={selectedItems.length === 0}
              style={{
                opacity: selectedItems.length === 0 ? 0.5 : 1,
                cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Proceed to Checkout ({selectedItems.length} items)
            </button>
            
            <button 
              className="normal" 
              onClick={clearCart}
              style={{ marginTop: '10px', background: '#f44336' }}
            >
              Clear Cart
            </button>
          </div>
        </section>
      )}
    </>
  );
};

export default Cart;